import styled, { css, keyframes } from "styled-components";
import { Typography } from "antd";
import { radius } from "@libertify/ui";
const { Title } = Typography;

// ─── Page-level wrappers ─────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  @media (width <= 1100px) {
    min-width: 840px;
  }

  @media (width <= 768px) {
    padding: 24px 16px;
    align-items: flex-start;
    padding-top: 100px;
    min-width: auto;
  }
`;

export const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 520px;
  margin-block-start: -64px;
`;

export const ViewContent = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 680px;
  margin-block-start: -64px;
  box-sizing: border-box;
  padding-inline: ${({ $isMobile }) => ($isMobile ? "8px" : "0")};
`;

export const HeaderGroup = styled.div<{ $hidden: boolean }>`
  overflow: hidden;
  max-height: ${(p) => (p.$hidden ? "0px" : "300px")};
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  transform: ${(p) => (p.$hidden ? "translateY(-10px)" : "translateY(0)")};
  transition:
    max-height 0.5s ease,
    opacity 0.35s ease,
    transform 0.35s ease;
`;

export const Headline = styled(Title)`
  && {
    font-size: 42px;
    font-weight: 600;
    color: #090e19;
    line-height: 1.13;
    letter-spacing: -1.2px;
    margin-bottom: 12px !important;

    @media (width <= 768px) {
      font-size: 28px;
      letter-spacing: -0.5px;
    }

    @media (width <= 480px) {
      font-size: 22px;
    }
  }
`;

export const Subtext = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  color: #6b7280;
  margin-bottom: 28px;
`;

export const SecurityNote = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #6ea29f;
  margin-top: 20px;
`;

// ─── View-level components (Prompt + shared) ─────────────────────────────────

export const ViewTag = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #6ea29f;
  text-align: center;
  margin: 0 0 16px;
  letter-spacing: 0.02em;
`;

export const ViewTitle = styled.h1<{ $tight?: boolean }>`
  font-family: "Poppins", sans-serif;
  font-size: 42px;
  font-weight: 600;
  color: #090e19;
  text-align: center;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0 0 ${({ $tight }) => ($tight ? "8px" : "32px")};

  @media (width <= 768px) {
    font-size: 34px;
  }

  @media (width <= 480px) {
    font-size: 26px;
  }
`;

export const MobileSubtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 19px;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
  line-height: 1.3;
  margin: 0 0 32px;
`;

export const BackLink = styled.button<{ $isMobile?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #14b8a6;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  align-self: flex-start;
  margin-bottom: 40px;
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    color: #164f4b;
  }

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      width: 40px;
      height: 40px;
      padding: 0;
      border-radius: ${radius.circle};
      border: 1px solid ${({ theme }) => theme.colorBorderSecondary};
      background: ${({ theme }) => theme.colorBgBase};
      justify-content: center;
      margin-bottom: 24px;
    `}
`;

export const FooterNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
`;

export const FooterText = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #6ea29f;
  margin: 0;
  text-align: center;
`;

// ─── Animation helpers ───────────────────────────────────────────────────────

export const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const AnimWord = styled.span<{
  $on: boolean;
  $d: number;
  $anim: boolean;
}>`
  display: inline-block;
  ${(p) => {
    if (!p.$on) return "opacity: 0;";
    if (!p.$anim) return "";
    return css`
      animation: ${fadeSlideUp} 640ms cubic-bezier(0.22, 1, 0.36, 1) both;
      animation-delay: ${p.$d}ms;
    `;
  }}
`;

export const Reveal = styled.div<{
  $on: boolean;
  $d: number;
  $anim: boolean;
}>`
  ${(p) => {
    if (!p.$on) return "opacity: 0;";
    if (!p.$anim) return "";
    return css`
      animation: ${fadeSlideUp} 640ms cubic-bezier(0.22, 1, 0.36, 1) both;
      animation-delay: ${p.$d}ms;
    `;
  }}
`;
